import { useEffect, useState } from 'react'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import LinearProgress from '@mui/joy/LinearProgress'
import Alert from '@mui/joy/Alert'
import StorageIcon from '@mui/icons-material/Storage'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import { FileSearchDocInfo, listFileSearchDocumentsForCompany } from 'api/oracleApi'

function fmtBytes(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return '0 B'
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function readMeta(doc: FileSearchDocInfo, key: string): string | undefined {
    const hit = doc.custom_metadata.find((m) => m.key === key)
    if (!hit) return undefined
    return hit.string_value ?? (hit.numeric_value != null ? String(hit.numeric_value) : undefined)
}

function shortName(name: string): string {
    const idx = name.lastIndexOf('/')
    return idx >= 0 ? name.slice(idx + 1) : name
}

function StateIcon({ state }: { state: FileSearchDocInfo['state'] }) {
    if (state === 'STATE_ACTIVE') return <CheckCircleIcon sx={{ fontSize: 12, color: 'success.400' }} />
    if (state === 'STATE_PENDING') return <HourglassEmptyIcon sx={{ fontSize: 12, color: 'warning.400' }} />
    if (state === 'STATE_FAILED') return <ErrorIcon sx={{ fontSize: 12, color: 'danger.400' }} />
    return <RadioButtonCheckedIcon sx={{ fontSize: 12, color: 'neutral.400' }} />
}

export function CompanyFileSearchDocs({ companyId }: { companyId: string }) {
    const [docs, setDocs] = useState<FileSearchDocInfo[] | null>(null)
    const [scanned, setScanned] = useState(0)
    const [truncated, setTruncated] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        setLoading(true)
        setError('')
        setDocs(null)
        let cancelled = false
        listFileSearchDocumentsForCompany(companyId)
            .then((r) => {
                if (cancelled) return
                setDocs(r.docs)
                setScanned(r.total_scanned)
                setTruncated(r.truncated)
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : String(err))
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [companyId])

    const factDocs = (docs ?? []).filter((d) => readMeta(d, 'doc_kind') === 'fact')
    const summaryDocs = (docs ?? []).filter((d) => readMeta(d, 'doc_kind') === 'summary')
    const sourceDocs = (docs ?? []).filter((d) => readMeta(d, 'doc_kind') === 'source')
    const otherDocs = (docs ?? []).filter((d) => {
        const k = readMeta(d, 'doc_kind')
        return k !== 'fact' && k !== 'summary' && k !== 'source'
    })

    return (
        <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
                <StorageIcon sx={{ fontSize: 16, color: 'text.tertiary' }} />
                <Typography level="title-sm">File Search Index</Typography>
                {docs && (
                    <Chip size="sm" variant="soft" sx={{ fontSize: 10, height: 18, '--Chip-paddingInline': '6px' }}>
                        {docs.length} doc{docs.length !== 1 ? 's' : ''}
                    </Chip>
                )}
                {truncated && (
                    <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: 10, height: 18 }}>
                        scan truncated at {scanned}
                    </Chip>
                )}
            </Stack>

            {loading && <LinearProgress size="sm" />}
            {error && (
                <Alert color="danger" size="sm">
                    {error}
                </Alert>
            )}

            {docs && docs.length === 0 && !loading && (
                <Typography level="body-xs" color="neutral" sx={{ fontFamily: 'monospace' }}>
                    — no documents indexed for this company —
                </Typography>
            )}

            {docs && docs.length > 0 && (
                <Stack gap={0.5}>
                    {summaryDocs.map((d) => (
                        <DocLine key={d.name} doc={d} />
                    ))}
                    {factDocs.map((d) => (
                        <DocLine key={d.name} doc={d} />
                    ))}
                    {sourceDocs.map((d) => (
                        <DocLine key={d.name} doc={d} />
                    ))}
                    {otherDocs.map((d) => (
                        <DocLine key={d.name} doc={d} />
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

function DocLine({ doc }: { doc: FileSearchDocInfo }) {
    const kind = readMeta(doc, 'doc_kind') ?? '—'
    const category = readMeta(doc, 'category')
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '20px 70px 1fr 60px 70px',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                fontFamily: 'monospace',
                fontSize: 10,
            }}
        >
            <StateIcon state={doc.state} />
            <Chip
                size="sm"
                variant="soft"
                color={kind === 'fact' ? 'primary' : kind === 'summary' ? 'success' : kind === 'source' ? 'warning' : 'neutral'}
                sx={{ fontSize: 9, height: 16, '--Chip-paddingInline': '5px' }}
            >
                {kind}
            </Chip>
            <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {doc.display_name || shortName(doc.name)}
                {category && <span style={{ color: 'var(--joy-palette-text-tertiary)', marginLeft: 6 }}>· {category}</span>}
            </Typography>
            <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: 9, color: 'text.tertiary', textAlign: 'right' }}>
                {fmtBytes(doc.size_bytes)}
            </Typography>
            <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: 9, color: 'text.tertiary', textAlign: 'right' }}>
                {doc.state.replace('STATE_', '').toLowerCase()}
            </Typography>
        </Box>
    )
}
